import React, { useState } from 'react';
import Reveal from './Reveal';
import './courseKit.css';

/**
 * The syllabus. Every course page we tore down (Udemy, Coursera, Harvard) puts
 * this front and centre, and Udemy shows lesson count plus duration, so we do
 * the same. Sessions come from course_session; nothing here is hardcoded.
 */
/**
 * Session length. The bootcamp-data endpoint returns the legacy free-text
 * `duration` column ("90 minutes", "1-2 hours") and not `durationMinutes`, so
 * reading only the numeric field silently dropped both the per-week length and
 * the "hours of instruction" total. Prefer the number when it is there and
 * parse the string when it is not.
 */
/**
 * Session titles are stored as "Week 3: Describe a scan…" so they stand alone
 * in the portal, in emails and in the admin. This component already renders a
 * "Week 3" badge, so strip the prefix here rather than storing two variants.
 */
export const stripWeekPrefix = (title = '') => title.replace(/^\s*week\s*\d+\s*[:.\-–]\s*/i, '');

export const sessionMinutes = (session) => {
  if (Number.isFinite(session?.durationMinutes)) return session.durationMinutes;
  const raw = typeof session?.duration === 'string' ? session.duration : '';
  const hours = raw.match(/(\d+(?:\.\d+)?)\s*(?:-\s*\d+(?:\.\d+)?\s*)?h/i);
  if (hours) return Math.round(parseFloat(hours[1]) * 60);
  const mins = raw.match(/(\d+)\s*m/i);
  return mins ? parseInt(mins[1], 10) : 0;
};

/**
 * `sessionNoun` lets the self-paced page describe the same course_session rows
 * as "recorded sessions" while the live page keeps "live sessions". The
 * trailing note follows the same split: live buyers are told everything is
 * recorded; self-paced buyers are told they can watch on their own schedule.
 */
const CurriculumAccordion = ({
  sessions = [], formatDate, defaultOpen = 0, sessionNoun = 'live sessions', metaNote = 'all recorded',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!sessions.length) return null;

  const totalMinutes = sessions.reduce((sum, s) => sum + sessionMinutes(s), 0);
  const hours = Math.round((totalMinutes / 60) * 10) / 10;

  return (
    <div className="ck-curriculum">
      <p className="ck-curriculum-meta">
        {sessions.length} {sessionNoun}
        {hours > 0 ? ` • ${hours} hours of instruction` : ''}
        {metaNote ? ` • ${metaNote}` : ''}
      </p>

      <ol className="ck-curriculum-list">
        {sessions.map((session, index) => {
          const isOpen = open === index;
          const bullets = Array.isArray(session.outcomes) ? session.outcomes : [];
          return (
            <Reveal as="li" key={session.id || index} delay={index * 60}>
              <button
                type="button"
                className={`ck-week ${isOpen ? 'is-open' : ''}`.trim()}
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? -1 : index)}
              >
                <span className="ck-week-num">Week {index + 1}</span>
                <span className="ck-week-title">{stripWeekPrefix(session.title)}</span>
                <span className="ck-week-meta">
                  {session.scheduledAt && formatDate ? formatDate(session.scheduledAt) : ''}
                  {sessionMinutes(session) ? ` • ${sessionMinutes(session)} min` : ''}
                </span>
                <span className="ck-week-chevron" aria-hidden="true" />
              </button>

              <div className={`ck-week-body ${isOpen ? 'is-open' : ''}`.trim()}>
                <div className="ck-week-body-inner">
                  {session.subtitle ? <p className="ck-week-sub">{session.subtitle}</p> : null}
                  {bullets.length ? (
                    <ul>
                      {bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  ) : session.description ? (
                    <div
                      className="ck-week-desc"
                      // Session descriptions are authored by us in the admin, not user input.
                      dangerouslySetInnerHTML={{ __html: session.description }}
                    />
                  ) : null}
                  {session.homeworkAssignment ? (
                    <div className="ck-week-homework" data-testid="week-homework">
                      <strong>Homework</strong>
                      <p>{session.homeworkAssignment}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </Reveal>
          );
        })}
      </ol>
    </div>
  );
};

export default CurriculumAccordion;
